import { forwardRef } from "react";
import styled, { css } from "styled-components";
import * as CONSTANTS from "../../shared/constants";
import { DataAttributes } from "../../shared/types";

export type ButtonVariant =
  | "primary"
  | "primary-light"
  | "secondary"
  | "tertiary"
  | "light"
  | "secondary-small"
  | "secondary-x-small"
  | "back-button"
  | "link-button"
  | "neutral"
  | "icon"
  | "contrast";

export type Spacing = number;

type BaseButtonProps = {
  variant: ButtonVariant;
  ml?: Spacing;
  mr?: Spacing;
  mt?: Spacing;
  maskText?: boolean;
  testId?: string;
  disabled?: boolean;
} & DataAttributes;

export type RegularButtonProps = BaseButtonProps &
  JSX.IntrinsicElements["button"] & {
    variant: Exclude<ButtonVariant, "link-button">;
  };

export type LinkButtonProps = BaseButtonProps &
  JSX.IntrinsicElements["a"] & {
    variant: "link-button";
  };

export type ButtonProps = RegularButtonProps | LinkButtonProps;

const sharedStyles = css<ButtonProps>`
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  display: flex;
  height: 40px;
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.MEDIUM};
  font-size: ${({ theme }) => theme.FONT.SIZE.STANDARD};
  border-radius: 70px;
  transition: 0.2s ease-in-out;

  ${({ mt }) =>
    mt &&
    typeof mt === "number" &&
    css`
      margin-top: ${mt}px;
    `}

  ${({ ml }) =>
    ml &&
    typeof ml === "number" &&
    css`
      margin-left: ${ml}px;
    `}

  ${({ mr }) =>
    mr &&
    typeof mr === "number" &&
    css`
      margin-right: ${mr}px;
    `}
`;

const StyledButton = styled.button.attrs<RegularButtonProps>((props) => ({
  type: props.type ? props.type : "button", // Automatically set button to keep reload on forms from happening unless otherwise specified
}))<RegularButtonProps>`
  ${sharedStyles}
  padding: 10px 20px;

  ${({ variant, theme }) =>
    variant === "primary" &&
    css`
      color: ${theme.COLORS.GRAY_BLACK};
      background-color: ${theme.COLORS.PRIMARY};
      border-width: 0px;

      &:not(.disabled):hover {
        background-color: ${theme.COLORS.PRIMARY_DARK};
        transition: 0.2 ease-in-out;
      }

      &:disabled:hover {
        background-color: ${theme.COLORS.PRIMARY};
        border-width: 0px;
        opacity: 0.3;
      }

      &:disabled {
        background-color: ${theme.COLORS.PRIMARY};
        border-width: 0px;
        opacity: 0.3;
      }
    `}

  ${({ variant, theme }) =>
    variant === "primary-light" &&
    css`
      color: ${theme.COLORS.GRAY_BLACK};
      background-color: ${theme.COLORS.PRIMARY_LIGHT};
      border-width: 0px;

      &:not(.disable):hover {
        background-color: ${theme.COLORS.PRIMARY_DARK};
        transition: 0.2 ease-in-out;
      }

      &:disabled:hover {
        background-color: ${theme.COLORS.PRIMARY};
        border-width: 0px;
        opacity: 0.3;
      }

      &:disabled {
        background-color: ${theme.COLORS.PRIMARY};
        border-width: 0px;
        opacity: 0.3;
      }
    `}

  ${({ variant, theme }) =>
    variant === "secondary" &&
    css`
      color: ${theme.COLORS.GRAY_BLACK};
      background-color: ${theme.COLORS.WHITE};
      border: 1px solid ${theme.COLORS.GRAY_MEDIUM};

      &:hover {
        background-color: ${theme.COLORS.WHITE};
        transition: 0.2 ease-in-out;
        border: 1px solid ${theme.COLORS.GRAY};
      }

      &:disabled,
      &:disabled:hover {
        color: ${theme.COLORS.GRAY_BLACK};
        background-color: transparent;
        border: 1px solid ${theme.COLORS.GRAY_MEDIUM};
        opacity: 0.3;
        cursor: not-allowed;
      }
    `}

  ${({ variant, theme }) =>
    variant === "tertiary" &&
    css`
      height: 33px;
      padding: 0 24px;
      color: ${theme.COLORS.BLACK};
      background-color: ${theme.COLORS.TERTIARY_GREEN};
      border: 1px solid ${theme.COLORS.TERTIARY_GREEN};
      font-weight: ${theme.FONT.WEIGHT.REGULAR};
    `}

    ${({ variant, theme }) =>
    variant === "light" &&
    css`
      color: ${theme.COLORS.BLACK_GREY};
      background-color: transparent;
      border: 1px solid ${theme.COLORS.GRAY_MEDIUM};

      &:hover {
        background-color: transparent;
        border: 1px solid ${theme.COLORS.GRAY};
      }
    `}

  ${({ disabled, theme }) =>
    disabled &&
    css`
      background-color: ${theme.COLORS.GREY};
      border: 1px solid ${theme.COLORS.GRAY_MEDIUM};
      opacity: 0.3;

      &:hover {
        background-color: ${theme.COLORS.GREY};
        border: 1px solid ${theme.COLORS.GRAY_MEDIUM};
        opacity: 0.3;
      }
    `}
    ${({ variant, theme }) =>
    variant === "secondary-small" &&
    css`
      margin-right: ${theme.MARGIN.SM};
      text-transform: none;
      border-radius: 100px;
      background-color: ${theme.COLORS.WHITE};
      border: 1px solid ${theme.COLORS.GRAY_MEDIUM};
      font-weight: ${theme.FONT.WEIGHT.REGULAR};
      font-size: ${theme.FONT.SIZE.STANDARD};
      height: 36px;
      font-size:;
      padding: 0 16px;
      cursor: pointer;
      &:hover {
        transition: ${theme.TRANSITION.STANDARD_TRANSITION};
        border: 1px solid ${theme.COLORS.GRAY};
      }
    `}
    ${({ variant, theme }) =>
    variant === "secondary-x-small" &&
    css`
      text-transform: none;
      border-radius: 100px;
      background-color: ${theme.COLORS.WHITE};
      border: 1px solid ${theme.COLORS.GRAY_MEDIUM};
      font-weight: ${theme.FONT.WEIGHT.REGULAR};
      font-size: ${theme.FONT.SIZE.STANDARD};
      height: 33px;
      font-size:;
      padding: 0 16px;
      cursor: pointer;
      &:hover {
        transition: ${theme.TRANSITION.STANDARD_TRANSITION};
        border: 1px solid ${theme.COLORS.GRAY};
      }
    `}
    ${({ variant, theme }) =>
    variant === "back-button" &&
    css`
      color: ${theme.COLORS.GRAY_BLACK};
      background-color: transparent;
      border-style: none;
      padding: 0;
      font-weight: 400;
      justify-content: flex-start;
      margin-right: auto;
      height: 22px;
    `}

    ${({ variant }) =>
    variant === "neutral" &&
    css`
      font-size: "100%";
      font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
      border: 0;
      padding: 0;
      background: transparent;
      text-decoration: underline;
    `}

    ${({ variant, theme }) =>
    variant === "icon" &&
    css`
      border: 0;
      padding: 0;
      width: 40px;
      height: 40px;
      box-sizing: border-box;
      background: transparent;
      color: ${theme.COLORS.GRAY_BLACK};
      background-color: ${theme.COLORS.WHITE};
      border: 1px solid ${theme.COLORS.GRAY_MEDIUM};

      &:hover {
        background-color: ${theme.COLORS.GRAY_BLACK_6};
        transition: 0.2 ease-in-out;
        border: 1px solid ${theme.COLORS.GRAY};
      }

      &:disabled,
      &:disabled:hover {
        color: ${theme.COLORS.GRAY_BLACK};
        background-color: transparent;
        border: 1px solid ${theme.COLORS.GRAY_MEDIUM};
        opacity: 0.3;
        cursor: not-allowed;
      }

      &:focus {
        outline: none;
        border: 1px solid ${theme.COLORS.GRAY};
      }
    `}

    ${({ variant, theme }) =>
    variant === "contrast" &&
    css`
      color: ${theme.COLORS.WHITE};
      background-color: ${theme.COLORS.GRAY_BLACK};
      border: 1px solid ${theme.COLORS.GRAY_BLACK};

      &:not(:disabled):hover {
        background-color: ${theme.COLORS.WHITE};
        color: ${theme.COLORS.GRAY_BLACK};
        border: 1px solid ${theme.COLORS.GRAY_BLACK};
      }

      &:disabled,
      &:disabled:hover {
        background-color: #5b5857;
        color: ${theme.COLORS.WHITE};
        border: 1px solid #5b5857;
        cursor: not-allowed;
      }
    `}
`;

const StyledLink = styled.a.attrs((props) => ({
  target: props.target || "_self",
  rel:
    props.target === "_blank" ? ("noopener noreferrer" as string) : undefined,
  href: props.href || "#",
}))<LinkButtonProps>`
  ${sharedStyles}
  display: inline;
  line-height: 140%;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
`;

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>((props, ref) => {
  const {
    children,
    variant,
    ml,
    mr,
    mt,
    maskText,
    testId,
    id,
    className,
    title,
    ...rest
  } = props;

  // Filter only `data-*` attributes
  const dataAttributes = Object.keys(rest)
    .filter((key) => key.startsWith("data-"))
    .reduce(
      (acc, key) => {
        acc[key] = rest[key as keyof typeof rest];
        return acc;
      },
      {} as Record<string, string | number | boolean | undefined>,
    );

  if (variant === "link-button") {
    const linkProps = props as LinkButtonProps;
    return (
      <StyledLink
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={className}
        variant={variant}
        href={linkProps.href}
        ml={ml}
        mr={mr}
        mt={mt}
        title={title}
        {...(testId && { "data-testid": testId })}
        {...(id && { id })}
        {...dataAttributes}
        {...(linkProps.target && { target: linkProps.target })}
        {...(linkProps.rel && { rel: linkProps.rel })}
      >
        {maskText ? CONSTANTS.DEMO : children}
      </StyledLink>
    );
  }

  const buttonProps = props as RegularButtonProps;
  return (
    <StyledButton
      ref={ref as React.Ref<HTMLButtonElement>}
      className={className}
      variant={variant}
      onClick={buttonProps.onClick}
      disabled={buttonProps.disabled}
      type={buttonProps.type}
      ml={ml}
      mr={mr}
      mt={mt}
      title={title}
      {...(testId && { "data-testid": testId })}
      {...(id && { id })}
      {...dataAttributes}
    >
      {maskText ? CONSTANTS.DEMO : children}
    </StyledButton>
  );
});

Button.displayName = "Button";
